import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, MapPin, X, Send, CheckCircle2 } from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePortalSid } from './PortalLayout';
import type { Evento } from '@/lib/types';

const FAM_BADGE: Record<string, string> = {
  'Conecta Folha Business': 'bg-blue/15 text-blue-light',
  'BRM - BUY': 'bg-purple/15 text-purple',
  'Setorial': 'bg-pink/15 text-pink',
};

export function OportunidadesPortal() {
  const { user } = useAuth();
  const portalSid = usePortalSid();
  const sid = portalSid ?? user?.solicitanteId ?? '';
  const [params, setParams] = useSearchParams();
  const focoId = params.get('evento');
  const qc = useQueryClient();

  const eventosQ = useQuery({ queryKey: ['portal', 'eventos'], queryFn: () => api.eventos.listPortal().then(r => r.data) });
  const inscQ = useQuery({ queryKey: ['portal', 'inscricoes'], queryFn: () => api.inscricoes.list().then(r => r.data) });

  const interesses = useMemo(() => new Set(
    (inscQ.data ?? []).filter(i => i.status === 'Interesse comercial').map(i => i.eventoId).filter(Boolean) as string[]
  ), [inscQ.data]);

  const oportunidades = useMemo(() =>
    (eventosQ.data ?? [])
      .filter(e => !(e.solicitantesIds ?? []).includes(sid))
      .filter(e => e.dataInicio && new Date(e.dataInicio) >= new Date())
      .sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? '')),
    [eventosQ.data, sid]);

  const [aberto, setAberto] = useState<Evento | null>(null);
  const [obs, setObs] = useState('');
  const [confirmado, setConfirmado] = useState<string | null>(null);

  // foco vindo de query string
  if (focoId && !aberto && !confirmado) {
    const e = (eventosQ.data ?? []).find(x => x.id === focoId);
    if (e && !(e.solicitantesIds ?? []).includes(sid)) {
      setAberto(e);
      setParams({}, { replace: true });
    }
  }

  const enviar = useMutation({
    mutationFn: ({ eventoId, observacao }: { eventoId: string; observacao?: string }) =>
      api.eventos.interesse(eventoId, observacao),
    onSuccess: () => {
      setConfirmado(aberto?.nome ?? 'evento');
      setAberto(null);
      setObs('');
      qc.invalidateQueries({ queryKey: ['portal', 'inscricoes'] });
      setTimeout(() => setConfirmado(null), 8000);
    },
  });

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-bold text-text">Oportunidades</h1>
        <p className="text-sm text-text-2">Eventos da Apex fora do seu plano · {oportunidades.length} disponíveis</p>
      </header>

      {confirmado && (
        <div className="bg-green/10 border border-green/30 rounded-md p-3 text-xs text-green-700 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green" />
          <span>Interesse em <strong>{confirmado}</strong> registrado. Comercial entra em contato em até 1 dia útil.</span>
        </div>
      )}

      <div className="space-y-2">
        {oportunidades.map(e => {
          const ja = interesses.has(e.id);
          const dia = e.dataInicio ? new Date(e.dataInicio).getDate() : '—';
          const mes = e.dataInicio ? new Date(e.dataInicio).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase() : '';
          return (
            <div key={e.id} className="bg-gradient-to-r from-amber-50/40 to-orange-50/30 border border-dashed border-orange-300 rounded-lg p-3 flex items-center gap-3">
              <div className="w-14 p-1.5 bg-white border border-orange-300 rounded-md text-center">
                <div className="text-lg font-extrabold text-orange-600 leading-none">{dia}</div>
                <div className="text-[8px] uppercase font-bold text-orange-500 mt-0.5">{mes}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-bold text-orange-900 truncate">{e.nome}</span>
                  {e.familia && <span className={clsx('text-[9px] font-bold px-1.5 py-0.5 rounded uppercase', FAM_BADGE[e.familia] ?? 'bg-gray-100 text-gray-700')}>{e.familia.replace('Conecta Folha Business', 'Conecta')}</span>}
                </div>
                <div className="text-[10.5px] text-orange-700 mt-0.5">
                  {e.local && <span><MapPin className="w-2.5 h-2.5 inline mr-1" />{e.local}</span>}
                  {e.descricao && <span> · {e.descricao.slice(0, 80)}{e.descricao.length > 80 ? '…' : ''}</span>}
                </div>
              </div>
              {ja ? (
                <span className="text-[11px] font-bold text-green-700 bg-green/10 border border-green/30 px-3 py-1.5 rounded">
                  ✓ Interesse registrado
                </span>
              ) : (
                <button type="button" onClick={() => setAberto(e)}
                  className="text-[11px] font-bold text-white bg-gradient-to-r from-orange-500 to-amber-600 px-3 py-1.5 rounded-md whitespace-nowrap">
                  ✨ Quero participar
                </button>
              )}
            </div>
          );
        })}

        {oportunidades.length === 0 && !eventosQ.isLoading && (
          <div className="bg-panel border border-border rounded-xl p-8 text-center text-muted">
            <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
            Nenhuma oportunidade no momento — você já está em todos os eventos do calendário.
          </div>
        )}
      </div>

      {/* Modal de interesse */}
      {aberto && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold">✨ Demonstrar interesse</h3>
                  <p className="text-xs opacity-90 mt-1">no evento {aberto.nome}</p>
                </div>
                <button type="button" onClick={() => setAberto(null)} className="text-white/80 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-3 text-sm text-gray-700">
              <p>
                Vamos avisar o time Comercial da Apex que você tem interesse em participar deste evento.
                Eles entram em contato em até 1 dia útil pra apresentar valores, vagas disponíveis e formato de participação.
              </p>
              <div>
                <label className="text-[10.5px] font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Observação (opcional)</label>
                <textarea value={obs} onChange={e => setObs(e.target.value)} rows={3}
                  placeholder="Ex: quero participar com 5 convidados, queria saber sobre patrocínio…"
                  className="w-full text-sm rounded-md border border-gray-300 bg-gray-50 p-2.5 focus:bg-white focus:border-blue-400 outline-none" />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-2">
              <button type="button" onClick={() => setAberto(null)} className="px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 rounded-md">
                Cancelar
              </button>
              <button type="button"
                onClick={() => aberto && enviar.mutate({ eventoId: aberto.id, observacao: obs.trim() || undefined })}
                disabled={enviar.isPending}
                className="px-4 py-2 text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-600 rounded-md inline-flex items-center gap-1.5 disabled:opacity-50">
                <Send className="w-3.5 h-3.5" />
                {enviar.isPending ? 'Enviando…' : 'Enviar interesse'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
